import type { UploadKind } from '@/api/uploads.api';

/** How a crop is shaped on screen. The saved image is always a plain rectangle: `round` only changes the overlay. */
export type CropShape = 'rect' | 'round';

export interface ImageCropSpec {
  /** Width / height of the locked crop frame. */
  aspect: number;
  /** The largest size the saved image is written at. Smaller crops are never scaled UP (FR-121). */
  outputWidth: number;
  outputHeight: number;
  shape: CropShape;
}

const SQUARE_LOGO: ImageCropSpec = { aspect: 1, outputWidth: 512, outputHeight: 512, shape: 'round' };

/**
 * The locked crop shape for each upload kind that supports cropping (FR-121, A-110).
 *
 * Keyed by `UploadKind`, so every upload control that already knows its kind gets
 * the right frame without a second prop to keep in step. A kind with NO entry is
 * not cropped: the admin uploads (event cover, resource files and covers,
 * public-page section images), pitch deck and pitch video are deliberately absent.
 *
 * - Logos, avatars and headshots are stored square and shown as circles, so the frame is a circle.
 * - The detail cover is a LinkedIn-style banner (about 1600 x 460).
 * - The listing cover is 2:1 (800 x 400), the shape of the directory-card band (FR-293, A-325; was 4:1).
 * - The offer cover is 16:9 (1200 x 675).
 */
export const IMAGE_CROP_SPECS: Partial<Record<UploadKind, ImageCropSpec>> = {
  partnerLogo: SQUARE_LOGO,
  logo: SQUARE_LOGO,
  investorLogo: SQUARE_LOGO,
  investorProfilePhoto: SQUARE_LOGO,
  individualAvatar: SQUARE_LOGO,
  partnerCoverPhoto: { aspect: 1600 / 460, outputWidth: 1600, outputHeight: 460, shape: 'rect' },
  partnerListingCover: { aspect: 2, outputWidth: 800, outputHeight: 400, shape: 'rect' },
  partnerOfferCoverPhoto: { aspect: 16 / 9, outputWidth: 1200, outputHeight: 675, shape: 'rect' },
};

/** Only these are cropped; a PDF (offer cover) or anything else uploads untouched. */
export const CROPPABLE_MIME_TYPES: readonly string[] = ['image/png', 'image/jpeg'];

/** Zoom limits of the crop dialog's slider; 1 is "fit the frame". */
export const CROP_ZOOM = { MIN: 1, MAX: 3, STEP: 0.05 } as const;

/** JPEG quality for a cropped JPEG. PNG output is lossless and ignores it. */
export const CROP_JPEG_QUALITY = 0.92;

/** A file larger than this is refused before it is decoded, so a huge file cannot freeze the tab. */
export const CROP_MAX_SOURCE_BYTES = 25 * 1024 * 1024;
/**
 * An image that decodes to more pixels than this (about 100 megapixels) is refused before
 * the crop canvas and the cropper are built. The byte limit above is the only pre-decode guard.
 */
export const CROP_MAX_SOURCE_PIXELS = 100_000_000;

/**
 * The detail-page cover band's shape, wherever that cover is rendered as a cover
 * (FR-198, MYN-294). It is the SAME ratio as `IMAGE_CROP_SPECS.partnerCoverPhoto`,
 * so an image cropped to the recommended 1600 x 460 fills the band exactly and
 * `object-cover` has nothing left to crop, at any viewport width.
 *
 * An aspect ratio rather than the earlier fixed `h-64` (FR-116): a fixed height at
 * fluid width has a different ratio at every viewport, so no recommended size could
 * ever fit it - on a phone that cost roughly 60% of the image's width. The height is
 * now proportional instead (about 248 px at the page's max width, about 103 px on a
 * 390 px phone).
 *
 * Kept as a literal rather than derived from the spec above, because Tailwind's JIT
 * only emits classes it can see written out. `image-crop.test.ts` asserts the two
 * agree, which is what actually stops them drifting - they already sat in this file
 * together and still disagreed, which is the defect MYN-294 reported.
 */
export const PARTNER_COVER_BAND_ASPECT_CLASS = 'aspect-[1600/460]';

/**
 * The directory-card band's shape, and the listing-cover upload preview's (FR-293, A-325).
 * The SAME ratio as `IMAGE_CROP_SPECS.partnerListingCover`, so a cover cropped to the
 * recommended 800 x 400 fills the band exactly. A literal for the same reason as the
 * detail band above; `image-crop.test.ts` is what binds the two.
 */
export const PARTNER_LISTING_BAND_ASPECT_CLASS = 'aspect-[800/400]';
